import React from 'react';
import { BrowserRouter as Router, Navigate, Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import theme from './styles/theme';

// Slick Carousel CSS still ships globally for legacy admin/customer pages that haven't been migrated yet.
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { MarketingShell } from './components/layout/MarketingShell';
import { DashShell } from './components/layout/DashShell';
import { BareShell } from './components/layout/BareShell';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProfilePage from './pages/ProfilePage';
import OverviewPage from './pages/OverviewPage';
import WalletPage from './pages/WalletPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailsPage from './pages/OrderDetailsPage';
import InternalShipmentsPage from './pages/InternalShipmentsPage';
import InternalShipmentCreatePage from './pages/InternalShipmentCreatePage';
import InternalShipmentDetailsPage from './pages/InternalShipmentDetailsPage';
import ContactsPage from './pages/ContactsPage';
import ContactPage from './pages/ContactPage';
import ShippingSearchPage from './pages/ShippingSearchPage';
import ShippingOrderPage from './pages/ShippingOrderPage';
import ShippingPaymentPage from './pages/ShippingPaymentPage';
import TrackShipmentPage from './pages/TrackShipmentPage';
import ApiTokensPage from './pages/ApiTokensPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ServicesPage from './pages/ServicesPage';
import AboutPage from './pages/AboutPage';
import FAQPage from './pages/FAQPage';
import VolumetricWeightPage from './pages/VolumetricWeightPage';
import NotFoundPage from './pages/NotFoundPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WalletProvider } from './context/WalletContext';
import { OrderProvider } from './context/OrderContext';
import { LanguageProvider } from './context/LanguageContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { ThemeProvider as AppThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import './App.css';

// Admin panel components -- all stay on MUI per design scope (admin tree is out of scope).
import AdminLayout from './components/admin/AdminLayout';
import DashboardPage from './pages/admin/DashboardPage';
import AdminUsersPage from './pages/admin/UsersPage';
import CommissionsPage from './pages/admin/CommissionsPage';
import AdminOrdersPage from './pages/admin/OrdersPage';
import AdminInternalShipmentsPage from './pages/admin/InternalShipmentsPage';
import AdminInternalShipmentDetailPage from './pages/admin/InternalShipmentDetailPage';
import AdminInternalShipmentRatesPage from './pages/admin/InternalShipmentRatesPage';
import SettingsPage from './pages/admin/SettingsPage';
import ShippingConfirmationPage from './pages/ShippingConfirmationPage';
import UserDetailPage from './pages/admin/UserDetailPage';
import OrderDetailPage from './pages/admin/OrderDetailPage';
import AddUserPage from './pages/admin/AddUserPage';
import SupportInboxPage from './pages/admin/SupportInboxPage';
import AiConversationsPage from './pages/admin/AiConversationsPage';
import AiConversationDetailPage from './pages/admin/AiConversationDetailPage';
import AiTokenUsagePage from './pages/admin/AiTokenUsagePage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import EmailVerificationSuccessPage from './pages/EmailVerificationSuccessPage';
import AddressValidationPage from './pages/AddressValidationPage';
import AiShipmentPage from './pages/AiShipmentPage';
import ScrollToTop from './components/common/ScrollToTop';
import { warmupRecaptchaV3 } from './utils/recaptchaV3';
import { LEGACY_CUSTOMER_REDIRECTS } from './routes/customerRoutes';

// Admin guard: ensures only admins reach /admin/* routes.
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute>
    <AdminCheck>{children}</AdminCheck>
  </ProtectedRoute>
);

const AdminCheck: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (!isLoading && !user?.is_admin) {
      navigate('/', { state: { from: location }, replace: true });
    }
  }, [isLoading, user, navigate, location]);

  if (isLoading) return null;
  return user?.is_admin ? <>{children}</> : null;
};

// MUI ThemeProvider is scoped to /admin/* only, so customer-tree CSS reset comes
// solely from src/styles/design/tokens.css. This avoids the CssBaseline collision
// flagged by the architect review.
const AdminBranch: React.FC = () => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <AdminLayout />
  </ThemeProvider>
);

const LegacyTrackingRedirect: React.FC = () => {
  const { trackingNumber } = useParams();
  return <Navigate to={trackingNumber ? `/track/${encodeURIComponent(trackingNumber)}` : '/track'} replace />;
};

const App: React.FC = () => {
  React.useEffect(() => {
    warmupRecaptchaV3().catch(() => {
      // reCAPTCHA optional; surface failure at submit time instead.
    });
  }, []);

  return (
    <HelmetProvider>
      <AppThemeProvider>
      <AuthProvider>
        <WalletProvider>
          <OrderProvider>
            <LanguageProvider>
              <CurrencyProvider>
                <Router>
                  <ScrollToTop />
                  <Routes>
                    {/* Compatibility aliases for links sent by earlier backend versions. */}
                    {LEGACY_CUSTOMER_REDIRECTS.map(({ path, to }) => (
                      <Route key={path} path={path} element={<Navigate to={to} replace />} />
                    ))}
                    <Route path="track-order" element={<LegacyTrackingRedirect />} />
                    <Route path="track-order/:trackingNumber" element={<LegacyTrackingRedirect />} />

                    {/* Admin tree -- MUI ThemeProvider/CssBaseline scoped here only. */}
                    <Route
                      path="/admin"
                      element={
                        <AdminRoute>
                          <AdminBranch />
                        </AdminRoute>
                      }
                    >
                      <Route index element={<DashboardPage />} />
                      <Route path="users" element={<AdminUsersPage />} />
                      <Route path="users/add" element={<AddUserPage />} />
                      <Route path="users/:id" element={<UserDetailPage />} />
                      <Route path="commissions" element={<CommissionsPage />} />
                      <Route path="orders" element={<AdminOrdersPage />} />
                      <Route path="orders/:id" element={<OrderDetailPage />} />
                      <Route path="internal-shipments" element={<AdminInternalShipmentsPage />} />
                      <Route path="internal-shipments/:id" element={<AdminInternalShipmentDetailPage />} />
                      <Route path="internal-shipment-rates" element={<AdminInternalShipmentRatesPage />} />
                      <Route path="support" element={<SupportInboxPage />} />
                      <Route path="support/:uuid" element={<SupportInboxPage />} />
                      <Route path="ai/conversations" element={<AiConversationsPage />} />
                      <Route path="ai/conversations/:id" element={<AiConversationDetailPage />} />
                      <Route path="ai/token-usage" element={<AiTokenUsagePage />} />
                      <Route path="settings" element={<SettingsPage />} />
                    </Route>

                    {/* Marketing shell: Navbar + Footer (public + shipping flow). */}
                    <Route element={<MarketingShell />}>
                      <Route index element={<HomePage />} />
                      <Route path="shipping" element={<ShippingSearchPage />} />
                      <Route path="shipping-confirmation" element={<ShippingConfirmationPage />} />
                      <Route path="shipping-order" element={<ShippingOrderPage />} />
                      <Route path="shipping-payment" element={<ShippingPaymentPage />} />
                      <Route path="track" element={<TrackShipmentPage />} />
                      <Route path="track/:trackingNumber" element={<TrackShipmentPage />} />
                      <Route path="contact" element={<ContactPage />} />
                      <Route path="privacy" element={<PrivacyPage />} />
                      <Route path="terms" element={<TermsPage />} />
                      <Route path="services" element={<ServicesPage />} />
                      <Route path="about" element={<AboutPage />} />
                      <Route path="faq" element={<FAQPage />} />
                      <Route path="volumetric-weight" element={<VolumetricWeightPage />} />
                    </Route>

                    {/* Bare shell: auth pages (no chrome). */}
                    <Route element={<BareShell />}>
                      <Route path="login" element={<LoginPage />} />
                      <Route path="signup" element={<SignupPage />} />
                      <Route path="verify-email" element={<EmailVerificationPage />} />
                      <Route path="auth/email/verify/:userId/:token" element={<EmailVerificationSuccessPage />} />
                      <Route path="reset-password" element={<ResetPasswordPage />} />
                      <Route path="reset-password/:token" element={<ResetPasswordPage />} />
                    </Route>

                    {/* Dash shell: sidebar + topbar (protected). */}
                    <Route
                      element={
                        <ProtectedRoute>
                          <DashShell />
                        </ProtectedRoute>
                      }
                    >
                      <Route path="overview" element={<OverviewPage />} />
                      <Route path="profile" element={<ProfilePage />} />
                      <Route path="wallet" element={<WalletPage />} />
                      <Route path="orders" element={<OrdersPage />} />
                      <Route path="orders/:id" element={<OrderDetailsPage />} />
                      <Route path="internal-shipments" element={<InternalShipmentsPage />} />
                      <Route path="internal-shipments/new" element={<InternalShipmentCreatePage />} />
                      <Route path="internal-shipments/:id" element={<InternalShipmentDetailsPage />} />
                      <Route path="contacts" element={<ContactsPage />} />
                      <Route path="address-validation" element={<AddressValidationPage />} />
                      <Route path="api-tokens" element={<ApiTokensPage />} />
                      <Route path="ai-shipment" element={<AiShipmentPage />} />
                    </Route>

                    {/* 404 catch-all -- bare (no shell). */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Router>
              </CurrencyProvider>
            </LanguageProvider>
          </OrderProvider>
        </WalletProvider>
      </AuthProvider>
      </AppThemeProvider>
    </HelmetProvider>
  );
};

export default App;
